// ============================================================================
// MODULAR DESIGN PRINCIPLES (MANDATORY)
// ============================================================================
// This file strictly follows:
// 1. Single Responsibility Principle - Renders custom SVG bot icon
// 2. Separation of Concerns - Pure presentational component
// 3. Modular Architecture - Reusable, independent icon component
//
// Refer to: docs/architecture/modular-design-standards.md
// ============================================================================

import React from 'react';
import { Box } from '@mui/material';

/**
 * Custom SVG Bot Icon with smile and colorful gradient
 * 
 * Features:
 * - Professional bot design with friendly smile
 * - Gradient background (purple to blue)
 * - Responsive sizing
 * - Material-UI integration
 * 
 * @param {Object} props
 * @param {number} props.size - Icon size in pixels (default: 48)
 * @returns {JSX.Element} ChatBotIcon component
 */
const ChatBotIcon = ({ size = 48 }) => {
  return (
    <Box
      sx={{
        width: size,
        height: size,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        borderRadius: '50%',
        boxShadow: '0 4px 12px rgba(102, 126, 234, 0.3)',
        transition: 'transform 0.2s ease',
        '&:hover': {
          transform: 'scale(1.05)',
        }
      }}
    >
      <svg
        width={size * 0.6}
        height={size * 0.6}
        viewBox="0 0 100 100"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        {/* Bot Head */}
        <rect
          x="20"
          y="30"
          width="60"
          height="50"
          rx="10"
          fill="white"
          opacity="0.95"
        />
        
        {/* Antenna */}
        <line
          x1="50"
          y1="15"
          x2="50"
          y2="30"
          stroke="white"
          strokeWidth="3"
          strokeLinecap="round"
        />
        <circle cx="50" cy="12" r="4" fill="white" />
        
        {/* Left Eye */}
        <circle cx="38" cy="45" r="5" fill="#667eea" />
        <circle cx="39" cy="44" r="2" fill="white" opacity="0.8" />
        
        {/* Right Eye */}
        <circle cx="62" cy="45" r="5" fill="#667eea" />
        <circle cx="63" cy="44" r="2" fill="white" opacity="0.8" />
        
        {/* Happy Smile - Curved path */}
        <path
          d="M 35 60 Q 50 70 65 60"
          stroke="#667eea"
          strokeWidth="3"
          strokeLinecap="round"
          fill="none"
        />
        
        {/* Cheek blush - Left */}
        <ellipse
          cx="30"
          cy="58"
          rx="4"
          ry="3"
          fill="#ff9999"
          opacity="0.4"
        />
        
        {/* Cheek blush - Right */}
        <ellipse
          cx="70"
          cy="58"
          rx="4"
          ry="3"
          fill="#ff9999"
          opacity="0.4"
        />
        
        {/* Bot Ears - Left */}
        <rect
          x="12"
          y="45"
          width="8"
          height="15"
          rx="4"
          fill="white"
          opacity="0.9"
        />
        
        {/* Bot Ears - Right */}
        <rect
          x="80"
          y="45"
          width="8"
          height="15"
          rx="4"
          fill="white"
          opacity="0.9"
        />
        
        {/* Decorative Lines on Head */}
        <line
          x1="30"
          y1="33"
          x2="40"
          y2="33"
          stroke="#667eea"
          strokeWidth="2"
          strokeLinecap="round"
          opacity="0.3"
        />
        <line
          x1="60"
          y1="33"
          x2="70"
          y2="33"
          stroke="#667eea"
          strokeWidth="2"
          strokeLinecap="round"
          opacity="0.3"
        />
      </svg>
    </Box>
  );
};

export default ChatBotIcon;

